import { FormEvent, ReactNode, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, CalendarX, ExclamationCircle, InfoCircle } from "react-bootstrap-icons";
import { getPublicWebinars, savePrivacyConsent, getClientIP } from "../../services/Public";
import Footer from "../../components/Footer/Footer";
import logo from "../../assets/logo.png";
import "./public-common.css";
import "./register.css";
import WebinarItem from "../../components/WebinarItem/webinarItem";
import { IWebinar, parseJson } from "../../utils/webinar";

type Status = "loading" | "ready" | "not-found" | "not-available" | "ended";

function RegisterLayout({ children }: { children: ReactNode }) {
    return (
        <div className="public-page register-page">
            <main>
                <Link to="/">
                    <img src={logo} alt="Logo Maggioli" className="public-logo" />
                </Link>
                {children}
            </main>
            <Footer />
        </div>
    );
}

function StatusMessage({ icon, title, text, danger = false }: { icon: ReactNode; title: string; text: string; danger?: boolean }) {
    return (
        <RegisterLayout>
            <div className={`public-status${danger ? " danger" : ""}`}>
                {icon}
                <h2>{title}</h2>
                <p>{text}</p>
                <Link to="/" className="btn btn-primary text-white">
                    Vedi tutti i webinar
                </Link>
            </div>
        </RegisterLayout>
    );
}

export default function Register() {
    const { id } = useParams<{ id: string }>();
    const [status, setStatus] = useState<Status>("loading");
    const [webinar, setWebinar] = useState<IWebinar | null>(null);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [message, setMessage] = useState<{ text: string; success: boolean } | null>(null);

    useEffect(() => {
        getPublicWebinars()
            .then((res) => {
                if (res.data === undefined) {
                    setStatus("not-found");
                    return;
                }
                const found: IWebinar | undefined = res.data.find((item: IWebinar) => item.id.toString() === id);
                if (!found) {
                    setStatus("not-found");
                    return;
                }
                if (found.active === 0) {
                    setStatus("not-available");
                    return;
                }
                const end = new Date(found.end_date);
                end.setDate(end.getDate() + 1);
                if (Date.now() > end.getTime()) {
                    setStatus("ended");
                    return;
                }
                setWebinar(found);
                setStatus("ready");
            })
            .catch(() => setStatus("not-found"));
    }, [id]);

    const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (submitting) return;
        setMessage(null);

        const formData = new FormData(e.currentTarget);
        const payload: Record<string, any> = {};
        const responses: any[] = [];
        for (const [key, value] of formData.entries()) {
            if (key.includes("question_")) {
                responses.push({ questionKey: key.replace("question_", ""), responseText: value });
            } else if (key.includes("choice_")) {
                responses.push({ questionKey: key.replace("choice_", ""), answerKey: value });
            } else {
                payload[key] = value;
            }
        }
        payload["webinar_id"] = id;
        payload["responses"] = JSON.stringify(responses);

        const ambito = parseJson(webinar!.json_data).ambito;
        if (ambito) payload["ambito"] = ambito;

        setSubmitting(true);
        const ip = await getClientIP();
        if (ip === null) {
            setMessage({ text: "Errore nel recupero dell'IP", success: false });
            setSubmitting(false);
            return;
        }
        payload["ip"] = ip;

        try {
            const res = await savePrivacyConsent(payload);
            setMessage({ text: res.message, success: !!res.status });
        } catch {
            setMessage({ text: "Errore durante l'invio della registrazione", success: false });
        } finally {
            setSubmitting(false);
        }
    };

    if (status === "loading") {
        return (
            <RegisterLayout>
                <div className="public-spinner">
                    <div className="spinner-border" role="status" />
                </div>
            </RegisterLayout>
        );
    }

    if (status === "not-found") {
        return <StatusMessage danger icon={<ExclamationCircle />} title="Webinar non trovato" text="L'evento che cerchi non esiste o è stato rimosso." />;
    }

    if (status === "not-available") {
        return <StatusMessage icon={<InfoCircle />} title="Webinar non disponibile" text="Questo webinar non è al momento disponibile." />;
    }

    if (status === "ended") {
        return <StatusMessage icon={<CalendarX />} title="Webinar terminato" text="Il webinar è già terminato. Non è più possibile registrarsi." />;
    }

    return (
        <RegisterLayout>
            <Link to="/" className="public-back">
                <ArrowLeft /> Tutti i webinar
            </Link>

            <WebinarItem webinar={webinar!} />

            <form onSubmit={onSubmit} className="register-card">
                <h2>Inserisci i tuoi dati per registrarti</h2>

                <div className="row g-3">
                    <div className="col-sm-6">
                        <label htmlFor="reg-nome" className="form-label">Nome</label>
                        <input type="text" id="reg-nome" name="nome" placeholder="Es. Mario" autoComplete="given-name" className="form-control" required />
                    </div>
                    <div className="col-sm-6">
                        <label htmlFor="reg-cognome" className="form-label">Cognome</label>
                        <input type="text" id="reg-cognome" name="cognome" placeholder="Es. Rossi" autoComplete="family-name" className="form-control" required />
                    </div>
                    <div className="col-12">
                        <label htmlFor="reg-email" className="form-label">Email</label>
                        <input type="email" id="reg-email" name="email" placeholder="nome@esempio.it" autoComplete="email" className="form-control" required />
                    </div>
                </div>
                <input type="hidden" name="micro" value="1205" />
                <input type="hidden" name="macro" value="0012" />
                <input type="hidden" name="servizio" value="ISCRIZIONE WEBINAR" />
                <input type="hidden" name="area_tematica" value="AI" />
                <input type="hidden" name="trattamento" value="1" />

                <div className="register-consent">
                    <p>
                        Autorizzo l'invio di comunicazioni a scopo commerciale e di marketing nei limiti indicati
                        nell'
                        <a
                            target="_blank"
                            rel="noreferrer"
                            href="https://privacy.maggiolicloud.it/privacy/brainbitesmaggiolicloudit"
                        >
                            informativa
                        </a>
                        .
                    </p>

                    <div className="options">
                        <div className="form-check">
                            <input className="form-check-input" type="radio" name="commerciale" id="option1" value="1" />
                            <label className="form-check-label" htmlFor="option1">
                                Autorizzo
                            </label>
                        </div>

                        <div className="form-check">
                            <input className="form-check-input" type="radio" name="commerciale" id="option2" value="0" />
                            <label className="form-check-label" htmlFor="option2">
                                Non autorizzo
                            </label>
                        </div>
                    </div>
                </div>

                <p className="register-privacy" id="consenso-container">
                    Cliccando su "Invia" dichiari di aver letto la{" "}
                    <a
                        target="_blank"
                        rel="noreferrer"
                        href="https://privacy.maggiolicloud.it/privacy/brainbitesmaggiolicloudit"
                    >
                        privacy policy
                    </a>
                    .
                </p>

                <button type="submit" name="submit" className="btn btn-submit" disabled={submitting}>
                    {submitting && <span className="spinner-border spinner-border-sm me-2" role="status" />}
                    {submitting ? "Invio in corso..." : "Invia"}
                </button>

                {message && (
                    <div
                        className={message.success ? "alert alert-success" : "alert alert-danger"}
                        role="alert"
                        dangerouslySetInnerHTML={{ __html: message.text }}
                    />
                )}
            </form>
        </RegisterLayout>
    );
}
